import { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';

import { QualityCheckService } from '../services/QualityCheckService';
import { useContentEditorService } from './ContentEditorServiceContext';
import { useFatalError } from './FatalErrorContext';
import { useInlineCommentManager } from './InlineCommentManagerContext';
import { useInlineEditsManager } from './InlineEditsManagerContext';
import { useMplServiceClient } from './MplServiceClientContext';

const QualityCheckServiceContext = createContext<QualityCheckService | undefined>(undefined);

export { QualityCheckServiceContext };

export function QualityCheckServiceProvider({ children }: { children: ReactNode }) {
  const mpl = useMplServiceClient();
  const contentEditorService = useContentEditorService();
  const commentManager = useInlineCommentManager();
  const editsManager = useInlineEditsManager();
  const { displayFatalError } = useFatalError();
  const service = useMemo(
    () =>
      new QualityCheckService(
        mpl,
        contentEditorService,
        commentManager,
        editsManager,
        displayFatalError,
      ),
    [mpl, contentEditorService, commentManager, editsManager, displayFatalError],
  );

  useEffect(() => {
    return () => {
      service.close();
    };
  }, [service]);

  return (
    <QualityCheckServiceContext.Provider value={service}>
      {children}
    </QualityCheckServiceContext.Provider>
  );
}

export function useQualityCheckService(): QualityCheckService {
  const context = useContext(QualityCheckServiceContext);
  if (!context) {
    throw new Error('useQualityCheckService must be used within QualityCheckServiceProvider');
  }
  return context;
}
